"use client";

import React, { useEffect, useMemo, useState } from "react";
import { motion } from "framer-motion";
import { Loader2, MessageSquareText, Send, Users } from "lucide-react";
import { apiClient } from "@/lib/api-client";
import { PAD, RADIUS, SPRING_IN } from "@/lib/alert-theme";
import { getViewModeChrome } from "../view-mode-chrome";

interface AskUserDisplayProps {
  sessionId?: string;
  toolCallId?: string;
  args?: any;
  result?: any;
  status: "loading" | "success";
  // Replay / shared-snapshot mode. When true the card hides the
  // free-text input and the "Skip for now" button so the only thing
  // the viewer can click is one of the canonical pre-selected option
  // buttons. The recording's canonical answer plays back regardless
  // of which option was clicked (see backend `_await_ask_user_reply`),
  // but pinning the UI to those options keeps the buddy-tour beat
  // unambiguous — no "type your own answer" / "skip" affordances to
  // explain or anchor.
  isReadonly?: boolean;
  /** Signal sessions get emerald accents instead of icy blue. */
  signal?: boolean;
  viewMode?: string;
}

export function AskUserDisplay({
  sessionId,
  toolCallId,
  args,
  result,
  status,
  isReadonly = false,
  signal = false,
  viewMode,
}: AskUserDisplayProps) {
  const chrome = getViewModeChrome(viewMode ?? (signal ? "signal" : undefined));
  const FOCUS_RING_TONED = `focus-visible:outline-none focus-visible:ring-2 ${chrome.focusRing} focus-visible:ring-offset-2 focus-visible:ring-offset-background`;
  const [countdown, setCountdown] = useState(180);
  const [selected, setSelected] = useState<string | null>(null);
  const [customValue, setCustomValue] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errorMsg, setErrorMsg] = useState<string | null>(null);

  const question = args?.question ?? "";
  const options: string[] = Array.isArray(args?.options) ? args.options : [];
  const allowCustom = args?.allow_custom !== false;
  const argsReady = Boolean(question);

  useEffect(() => {
    if (status !== "loading" || selected || countdown <= 0) return;
    const timer = setInterval(() => setCountdown((prev) => prev - 1), 1000);
    return () => clearInterval(timer);
  }, [status, selected, countdown]);

  const answerText = useMemo(() => {
    if (status === "success") {
      if (result?.status === "skipped") {
        return "User skipped this question.";
      }
      const raw = result?.response ?? result?.message ?? selected ?? null;
      if (typeof raw === "string") {
        return raw.replace(/^\s*User replied with:\s*/i, "");
      }
      return raw;
    }
    if (selected === "__skip__") {
      return "User skipped this question.";
    }
    return selected;
  }, [result, selected, status]);

  const submitValue = async (value: string) => {
    if (!sessionId || !toolCallId || !value || isSubmitting || countdown <= 0) return;
    setErrorMsg(null);
    try {
      setIsSubmitting(true);
      setSelected(value);
      await apiClient.submitAskUserReply(sessionId, toolCallId, value);
    } catch (err: any) {
      console.error("[AskUser] submit failed", err);
      setSelected(null);
      setErrorMsg(
        err?.message?.includes("401")
          ? "Not signed in. Please refresh and sign in again."
          : err?.message || "Couldn't send your answer. Try again.",
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  // ── Answered — one-line matte Xynth card: people icon + concise reply ──
  if (status === "success" || answerText) {
    const skipped = result?.status === "skipped" || selected === "__skip__";
    return (
      <motion.div
        {...SPRING_IN}
        className="w-full"
        // Buddy-anchor hooks. `data-state` flips to "answered" /
        // "skipped" so the buddy script's `active`-only selector
        // stops matching this card the moment the viewer responds.
        data-testid="ask-user-display"
        data-state={skipped ? "skipped" : "answered"}
      >
        <div className={`${RADIUS.card} border ${chrome.card} overflow-hidden`}>
          <div className="px-3 py-2 flex items-center gap-2.5 min-w-0">
            <Users className={`w-3.5 h-3.5 shrink-0 ${chrome.iconMuted}`} strokeWidth={2} />
            <span className={`text-[13px] ${chrome.text} truncate min-w-0`}>
              {skipped ? String(answerText) : `User replied with: ${String(answerText)}`}
            </span>
          </div>
        </div>
      </motion.div>
    );
  }

  // Gate: until args.question arrives, show inline loader
  if (!argsReady) {
    return (
      <motion.div
        {...SPRING_IN}
        className="w-full"
        // `loading` (args not ready yet) — buddy script's
        // [data-state="active"] selector deliberately doesn't match
        // this state so the spotlight doesn't land on the empty
        // skeleton before the question text streams in.
        data-testid="ask-user-display"
        data-state="loading"
      >
        <div className={`${RADIUS.card} border ${chrome.card} overflow-hidden`}>
          <div className={`${PAD.default} flex items-center gap-3`}>
            <div
              className={`w-7 h-7 ${RADIUS.chip} bg-white/[0.05] flex items-center justify-center shrink-0`}
            >
              <Loader2
                className={`w-3.5 h-3.5 ${chrome.iconMuted} animate-spin`}
                strokeWidth={2}
              />
            </div>
            <span className={`text-sm ${chrome.subtext}`}>Preparing question…</span>
          </div>
        </div>
      </motion.div>
    );
  }

  // ── Active question — SLATE surface with icy accents on interactive parts ──
  return (
    <motion.div
      {...SPRING_IN}
      className="w-full"
      // `active` is the state the buddy script anchors to. Only one
      // AskUserDisplay is in this state at a time (the backend gates
      // ask_user calls sequentially), so the selector resolves
      // unambiguously.
      data-testid="ask-user-display"
      data-state="active"
    >
      <div className={`${RADIUS.card} border ${chrome.card} overflow-hidden shadow-sm`}>
        <div className={`${PAD.confirm} flex flex-col gap-3`}>
          <div className="flex items-center gap-3 min-w-0">
            <div
              className={`w-9 h-9 ${RADIUS.chip} bg-white/[0.05] flex items-center justify-center shrink-0`}
            >
              <MessageSquareText
                className={`w-4 h-4 ${chrome.iconMuted}`}
                strokeWidth={2}
              />
            </div>
            <div className="flex flex-col min-w-0 flex-1 gap-0.5">
              <span className={`text-sm font-semibold ${chrome.text} leading-tight`}>{question}</span>
              <span className={`text-xs ${chrome.subtext} leading-snug`}>
                Pick one, type your own, or skip this question. Auto-timeout in{" "}
                <span className="tabular-nums">{countdown}s</span>
              </span>
            </div>
            {isSubmitting && (
              <Loader2 className={`h-4 w-4 animate-spin ${chrome.iconMuted} shrink-0`} />
            )}
          </div>

          <div className="flex flex-col gap-1.5">
            {options.map((option, index) => (
              <button
                key={option}
                type="button"
                data-testid="ask-user-option"
                data-option-index={index}
                disabled={isSubmitting || countdown <= 0}
                onClick={() => submitValue(option)}
                className={`${RADIUS.button} ${chrome.optionBtn} px-3 py-2 text-left text-sm disabled:opacity-50 disabled:cursor-not-allowed transition-colors ${FOCUS_RING_TONED}`}
              >
                {option}
              </button>
            ))}
          </div>

          {/* Replay / readonly mode pins the card to the pre-selected
              option buttons only — no skip, no custom input. The
              buddy-tour beat that anchors here doesn't have to
              explain "you can also type your own answer or skip"
              affordances that wouldn't actually do anything in a
              recorded session. Live chats still see both. */}
          {!isReadonly && (
            <div className={`pt-1 border-t ${chrome.divider} flex items-center justify-between gap-3`}>
              <span className={`text-[11px] ${chrome.subtext}`}>
                Not sure? You can skip.
              </span>
              <button
                type="button"
                disabled={isSubmitting || countdown <= 0}
                onClick={() => submitValue("__skip__")}
                className={`inline-flex items-center justify-center h-8 px-3 ${RADIUS.button} ${chrome.optionBtn} text-[12px] font-medium disabled:opacity-50 disabled:cursor-not-allowed transition-colors whitespace-nowrap ${FOCUS_RING_TONED}`}
              >
                Skip for now
              </button>
            </div>
          )}

          {allowCustom && !isReadonly && (
            <div className="flex items-center gap-2">
              <input
                value={customValue}
                onChange={(e) => setCustomValue(e.target.value)}
                onKeyDown={(e) => {
                  // Enter submits the typed answer (same guards as the Send
                  // button). Without this, Enter does nothing and the user has
                  // to click Send manually.
                  if (e.key === "Enter") {
                    e.preventDefault();
                    const v = customValue.trim();
                    if (v && !isSubmitting && countdown > 0) submitValue(v);
                  }
                }}
                placeholder="Type your own answer"
                disabled={isSubmitting || countdown <= 0}
                className={`flex-1 ${RADIUS.button} border ${chrome.divider} bg-white/[0.03] px-3 py-2 text-sm ${chrome.text} placeholder:text-white/45 outline-none ${chrome.inputFocus} transition-colors ${FOCUS_RING_TONED}`}
              />
              <button
                type="button"
                disabled={!customValue.trim() || isSubmitting || countdown <= 0}
                onClick={() => submitValue(customValue.trim())}
                aria-label="Send answer"
                className={`inline-flex items-center justify-center h-9 w-9 ${RADIUS.button} ${chrome.sendBtn} text-emerald-950 disabled:opacity-40 disabled:cursor-not-allowed transition-colors ${FOCUS_RING_TONED}`}
              >
                <Send className="h-4 w-4" />
              </button>
            </div>
          )}

          {errorMsg && (
            <div className={`text-[11px] ${chrome.subtext}`}>{errorMsg}</div>
          )}
        </div>
      </div>
    </motion.div>
  );
}
